import React, { useState, useRef, useEffect } from 'react';

interface LayerNameInputProps {
  name: string;
  onSave: (value: string) => void;
  disabled?: boolean;
  autoEdit?: boolean;
}

export const LayerNameInput: React.FC<LayerNameInputProps> = ({
  name,
  onSave,
  disabled = false,
  autoEdit = false,
}) =>
{
  const [editing, setEditing] = useState(autoEdit);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  // Prevents onBlur from double-committing when Tab or Enter already committed
  const committedRef = useRef(false);

  useEffect(() =>
  {
    setValue(name);
  }, [name]);

  useEffect(() =>
  {
    if (editing && inputRef.current)
    {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () =>
  {
    const trimmed = value.trim();
    if (!trimmed)
    {
      setValue(name);
    }
    else
    {
      onSave(trimmed);
    }
    setEditing(false);
  };

  const cancel = () =>
  {
    setValue(name);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter')
    {
      committedRef.current = true;
      commit();
    }
    else if (e.key === 'Escape')
    {
      committedRef.current = true;
      cancel();
    }
    else if (e.key === 'Tab')
    {
      // Commit but allow Tab to propagate so focus moves to the next element
      committedRef.current = true;
      commit();
    }
  };

  const handleBlur = () =>
  {
    if (!committedRef.current)
    {
      commit();
    }
    committedRef.current = false;
  };

  const handleDoubleClick = () =>
  {
    if (!disabled) setEditing(true);
  };

  if (editing)
  {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="border border-[#62aacb] rounded px-1 py-0.5 text-sm text-[#333] outline-none w-full max-w-[200px]"
      />
    );
  }

  return (
    <span
      title={disabled ? undefined : 'Double click to rename'}
      onDoubleClick={handleDoubleClick}
      className={`text-sm font-medium text-[#333] select-none ${!disabled ? 'cursor-text hover:underline hover:decoration-dotted' : ''}`}
    >
      {name}
    </span>
  );
};
